import * as React from 'react';

import {
  CreateFinancialAccountInput,
  PaymentAllocationMethod,
  RoundingMethod,
  RoundingPrecision,
  UpdateFinancialAccountInput,
  enumCurrencyCode,
  enumFinancialAccountType,
  enumPaymentAllocationMethod,
  enumRoundingMethod,
  enumRoundingPrecision,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { FinancialAccountForm } from './FinancialAccountForm';

import { useTranslations } from '@/finance/i18n/useTranslations';
import * as formState from '~/modules/finance/_state/financialAccountForm.state';
import * as state from '~/modules/finance/_state/financialAccounts.state';
import { FinancialAccountRecord } from '~/modules/finance/types';
type FinancialAccountDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  account?: FinancialAccountRecord;
  refetch: () => void;
};

export const FinancialAccountDialog = ({
  isOpen,
  onClose,
  account,
  refetch,
}: FinancialAccountDialogProps) => {
  const t = useTranslations();
  const toast = useToast();
  const form = useAtomValue(formState.$financialAccountForm);
  const updateForm = useSetAtom(formState.$updateFinancialAccountForm);
  const createAccount = useSetAtom(state.$createFinancialAccount);
  const updateAccount = useSetAtom(state.$updateFinancialAccount);

  React.useEffect(() => {
    if (account) {
      updateForm({
        name: account.name,
        type: account.type,
        currentBalance: account.currentBalance.amount,
        warningThreshold: account.currentBalance.amount,
        currency: account.currency,
        visibleInPayments: account.visibleInPayments,
        roundingMethod: account.roundingMethod as RoundingMethod,
        roundingPrecision: account.roundingPrecision as RoundingPrecision,
        facilityId: account.facilityId,
        nachaCompanyIdentification: account.nachaCompanyIdentification ?? '',
        nachaCompanyName: account.nachaCompanyName ?? '',
        nachaDefaultSecCode: account.nachaDefaultSecCode ?? '',
        paymentAllocationMethod:
          account.paymentAllocationMethod as PaymentAllocationMethod,
      });
    }
  }, [account, updateForm]);

  const handleClose = () => {
    onClose();
    updateForm({
      name: '',
      type: enumFinancialAccountType.Checking,
      currentBalance: 0,
      warningThreshold: 0,
      currency: enumCurrencyCode.USD,
      visibleInPayments: true,
      roundingMethod: enumRoundingMethod.Up,
      roundingPrecision: enumRoundingPrecision.Hundred,
      facilityId: null,
      nachaCompanyIdentification: '',
      nachaCompanyName: '',
      nachaDefaultSecCode: '',
      paymentAllocationMethod: enumPaymentAllocationMethod.WeightedDistribution,
    });
  };

  const handleSave = async () => {
    try {
      if (account) {
        const input: UpdateFinancialAccountInput = {
          name: form.name,
          type: form.type,
          visibleInPayments: form.visibleInPayments,
          warningThreshold: form.warningThreshold,
          roundingMethod: form.roundingMethod as RoundingMethod,
          roundingPrecision: form.roundingPrecision as RoundingPrecision,
          nachaCompanyIdentification:
            form.nachaCompanyIdentification || undefined,
          nachaCompanyName: form.nachaCompanyName || undefined,
          nachaDefaultSecCode: form.nachaDefaultSecCode || undefined,
          paymentAllocationMethod:
            form.paymentAllocationMethod as PaymentAllocationMethod,
        };

        await updateAccount({ id: account.id, ...input });
        toast.success(t.messages.accounts.updateSuccess);
        refetch();
      } else {
        if (!form.facilityId) {
          toast.error(t.messages.accounts.facilityRequired);
          return;
        }

        const input: CreateFinancialAccountInput = {
          name: form.name,
          type: form.type,
          currency: form.currency,
          visibleInPayments: form.visibleInPayments,
          roundingMethod: form.roundingMethod as RoundingMethod,
          roundingPrecision: form.roundingPrecision as RoundingPrecision,
          initialBalance: form.currentBalance,
          warningThreshold: form.currentBalance,
          facilityID: form.facilityId,
          nachaCompanyIdentification:
            form.nachaCompanyIdentification || undefined,
          nachaCompanyName: form.nachaCompanyName || undefined,
          nachaDefaultSecCode: form.nachaDefaultSecCode || undefined,
          paymentAllocationMethod:
            form.paymentAllocationMethod as PaymentAllocationMethod,
        };

        await createAccount(input);
        toast.success(t.messages.accounts.createSuccess);
        refetch();
      }
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.messages.accounts.saveError,
      );
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onClose}
      className="@FinancialAccountDialog"
    >
      <DialogContent position="center">
        <DialogHeader slot="title">
          <DialogTitle>
            {account
              ? t.financialAccounts.dialog.title.edit
              : t.financialAccounts.dialog.title.create}
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col">
          <div className="p-4">
            <FinancialAccountForm isEditing={!!account} />
          </div>

          <div className="flex-none border-t border-neutral/20 bg-neutral/5 p-4">
            <div className="flex justify-end gap-3">
              <Button variant="outline" onPress={handleClose}>
                {t.financialAccounts.dialog.buttons.cancel}
              </Button>
              <Button onPress={handleSave} color="primary">
                {account
                  ? t.financialAccounts.dialog.buttons.save
                  : t.financialAccounts.dialog.buttons.create}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
